import React from 'react'
import { reportFailure } from '../../../services/report-failure'
import { isStaleChunkError, recoverFromStaleChunk } from '../../../utils/chunkLoadRecovery'
import Preloader from '../../Preloader/Preloader'
import ErrorFallback from './ErrorFallback'

const UPDATED_TITLE = 'A new version of Studio is available.'

// Generic error boundary. Wrap subtrees that can throw during render (e.g. the
// screenplay preview) so a failure degrades to a fallback instead of tearing
// down the whole app — which previously read as the Create Scene modal
// "closing unexpectedly".
//
// `feature` names the surface in the report and the default fallback; a new
// `resetKey` (e.g. the route path) clears a caught error.
class ErrorBoundary extends React.Component {
  constructor (props) {
    super(props)
    this.state = { hasError: false, staleChunk: false, reloadBlocked: false }
  }

  static getDerivedStateFromError (error) {
    return { hasError: true, staleChunk: isStaleChunkError(error) }
  }

  componentDidUpdate (prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, staleChunk: false, reloadBlocked: false })
    }
  }

  componentDidCatch (error, info) {
    // A tab from before the last deploy asked for a chunk that is gone: reload onto
    // the new build behind the loading screen. Not a bug, so nothing is reported.
    if (this.state.staleChunk) {
      if (recoverFromStaleChunk()) return
      this.setState({ reloadBlocked: true })
    } else {
      console.error('ErrorBoundary caught an error', error, info)
      reportFailure({
        feature: this.props.feature,
        scenario: this.props.scenario || 'render',
        error,
        extra: { componentStack: info?.componentStack }
      })
    }
    if (typeof this.props.onError === 'function') {
      this.props.onError(error, info)
    }
  }

  render () {
    const { hasError, staleChunk, reloadBlocked } = this.state
    if (!hasError) return this.props.children
    if (staleChunk && !reloadBlocked) return <Preloader />
    if (this.props.fallback !== undefined) return this.props.fallback
    return staleChunk
      ? <ErrorFallback title={UPDATED_TITLE} />
      : <ErrorFallback feature={this.props.feature} />
  }
}

export default ErrorBoundary
